/* Cart intent and Shopify's cart are separate until a read confirms them.
 * Serialize theme mutations, verify before checkout, and never clear an accepted
 * cart to attempt recovery. Restoration is explicit and returns to cart review.
 */
(() => {
  const STORAGE_KEY = 'pure-cart-recovery-v2';
  const CHANGE_KEY = 'pure-cart-changed-v2';
  const MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const ADD_SOURCES = new Set(['product-form', 'cart-drawer-upsell']);
  const scope = document.querySelector('[data-pure-cart-recovery-lines]')?.dataset?.cartRecoveryScope || 'guest';
  let memory = null;
  let revision = 0;
  let verification = Promise.resolve();
  let mutations = Promise.resolve();
  let checkoutAttempt = null;
  let locked = false;
  let externalChange = false;
  let message = '';
  let requestedMutation = 0;
  let leaving = false;

  const cartUrl = () => (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
  const storage = {
    get() {
      try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) memory = JSON.parse(raw);
      } catch { /* In-memory intent still protects this page when storage is blocked. */ }
      if (!memory || memory.scope !== scope || !Number.isFinite(memory.updatedAt) ||
          Date.now() - memory.updatedAt > MAX_AGE_MS || !Array.isArray(memory.lines)) return null;
      return { ...memory, lines: memory.lines.map(normalizeLine).filter(Boolean) };
    },
    set(state) {
      memory = { ...state, scope, updatedAt: Date.now() };
      try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch { /* Keep memory. */ }
      return memory;
    },
  };

  // Callers include response handling and publication inside the operation.
  // A rejected request cannot poison the queue or block later retries.
  function mutate(operation) {
    requestedMutation += 1;
    const pending = mutations.then(async () => {
      if (locked) await checkoutAttempt;
      return operation();
    });
    mutations = pending.catch(() => {});
    return pending;
  }

  async function waitForIdle() {
    let pendingMutations;
    let pendingVerification;
    do {
      pendingMutations = mutations;
      pendingVerification = verification;
      await pendingMutations;
      await pendingVerification;
    } while (pendingMutations !== mutations || pendingVerification !== verification);
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};
    return Object.fromEntries(
      Object.entries(properties)
        .filter(([, value]) => value !== null && value !== '')
        .sort(([left], [right]) => left.localeCompare(right))
    );
  }

  function normalizeLine(item) {
    const id = Number(item?.variant_id ?? item?.id);
    const quantity = Number(item?.quantity);
    if (!Number.isInteger(id) || id <= 0 || !Number.isFinite(quantity) || quantity <= 0) return null;

    const sellingPlan = Number(
      item?.selling_plan_allocation?.selling_plan?.id ?? item?.selling_plan ?? 0
    );

    return {
      id,
      quantity,
      selling_plan: Number.isInteger(sellingPlan) && sellingPlan > 0 ? sellingPlan : null,
      properties: normalizeProperties(item?.properties),
    };
  }

  function lineKey(line) {
    return JSON.stringify([line.id, line.selling_plan, line.properties]);
  }

  function mergeLines(base, additions) {
    const merged = new Map();
    for (const candidate of [...base, ...additions]) {
      const line = normalizeLine(candidate);
      if (!line) continue;
      const key = lineKey(line);
      const existing = merged.get(key);
      if (existing) existing.quantity += line.quantity;
      else merged.set(key, line);
    }
    return [...merged.values()];
  }

  function containsLines(actual, expected) {
    const quantities = new Map();
    for (const candidate of actual) {
      const line = normalizeLine(candidate);
      if (!line) continue;
      const key = lineKey(line);
      quantities.set(key, (quantities.get(key) || 0) + line.quantity);
    }
    return expected.every((line) => (quantities.get(lineKey(line)) || 0) >= line.quantity);
  }

  function parseRenderedCart() {
    const node = document.querySelector('[data-pure-cart-recovery-lines]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent).map(normalizeLine).filter(Boolean);
    } catch {
      return [];
    }
  }

  function sameLines(actual, expected) {
    return containsLines(actual, expected) && containsLines(expected, actual);
  }

  function missingLines(actual, expected) {
    const quantities = new Map(mergeLines([], actual).map(line => [lineKey(line), line.quantity]));
    return expected.map(line => ({ ...line, quantity: Math.max(0, line.quantity - (quantities.get(lineKey(line)) || 0)) }))
      .filter(line => line.quantity > 0);
  }

  function applyState() {
    const state = storage.get();
    const failed = Boolean(state?.failed);
    const targets = [...document.querySelectorAll('button[name="checkout"]')].map(button => ({
      button, host: button.parentElement || button.form || button.closest('form'),
    }));
    document.querySelectorAll('[data-pure-cart-recovery-host]').forEach(host => {
      host.hidden = !failed && !message;
      targets.push({ host, button: null });
    });
    targets.forEach(({ button, host }) => {
      button?.toggleAttribute('data-cart-session-recovery', failed);
      if (failed && !locked) button?.removeAttribute('disabled');
      if (!host) return;
      let panel = host.querySelector('[data-cart-session-recovery-status]');
      if (!failed && !message) { panel?.remove(); return; }
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'pure-cart__recovery-status';
        panel.dataset.cartSessionRecoveryStatus = '';
        panel.setAttribute('role', 'status');
        panel.setAttribute('aria-live', 'polite');
        if (button) button.before(panel);
        else host.appendChild(panel);
      }
      panel.replaceChildren();
      const text = document.createElement('p');
      text.textContent = message || 'Some items could not be confirmed in your cart. Review your cart or try restoring the missing items before checkout.';
      panel.appendChild(text);
      if (failed && state?.lines.length && !externalChange) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        retry.textContent = 'Restore missing items';
        retry.disabled = locked;
        retry.addEventListener('click', () => { void recoverCheckout(button); });
        panel.appendChild(retry);
      }
      const review = document.createElement('button');
      review.type = 'button';
      review.className = 'button button--secondary';
      review.textContent = 'Review current cart';
      review.disabled = locked;
      review.addEventListener('click', () => { void reviewCart(button); });
      panel.appendChild(review);
      const help = document.createElement('a');
      help.href = `${cartUrl().replace(/\/cart$/, '')}/pages/contact-us`;
      help.textContent = 'Get help with your order';
      help.className = 'link';
      panel.appendChild(help);
    });
  }

  function additionsFromEvent(event) {
    if (!ADD_SOURCES.has(event?.source)) return [];
    const data = event.cartData;
    const additions = (Array.isArray(data?.items) ? data.items : [data]).map(normalizeLine).filter(Boolean);
    const addedQuantity = Number(event.addedQuantity);
    if (additions.length === 1 && Number.isInteger(addedQuantity) && addedQuantity > 0) additions[0].quantity = addedQuantity;
    return additions;
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        const error = new Error('Cart request failed');
        if (options.method === 'POST' && typeof data.description === 'string') {
          error.cartMessage = `Shopify could not restore an item: ${data.description} Your existing items have not been cleared.`;
        }
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }

  async function fetchCart() {
    try {
      const cart = await request(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      return Array.isArray(cart.items) ? cart.items.map(normalizeLine).filter(Boolean) : null;
    } catch { return null; }
  }

  function announceChange() {
    try { window.localStorage.setItem(CHANGE_KEY, `${Date.now()}:${Math.random()}`); } catch { /* Optional cross-tab signal. */ }
  }

  function verifyCartUpdate(event) {
    if (event?.cartData?.errors || event?.cartData?.status) return Promise.resolve();
    const additions = additionsFromEvent(event);
    const current = storage.get() || { failed: false, lines: parseRenderedCart() };
    if (!additions.length && !Array.isArray(event?.cartData?.items)) return Promise.resolve();
    const expected = additions.length ? mergeLines(current.lines, additions)
      : event.cartData.items.map(normalizeLine).filter(Boolean);
    const version = ++revision;
    message = '';
    // Record intent synchronously. Never import a read's extra lines: they may
    // belong to a later add whose event has not been delivered yet.
    storage.set({ failed: additions.length ? current.failed : false, lines: expected });
    announceChange();
    const pending = verification.catch(() => {}).then(async () => {
      if (version !== revision) return;
      const actual = await fetchCart();
      if (version !== revision) return;
      storage.set({ failed: !actual || !containsLines(actual, expected), lines: expected });
      if (!actual) message = 'We could not check your cart. Please try again before checkout.';
      applyState();
    });
    verification = pending;
    return pending;
  }

  function submitNormalCheckout(button) {
    const form = button?.form || button?.closest?.('form');
    if (!form) { window.location.assign(`${cartUrl().replace(/\/cart$/, '')}/checkout`); return; }
    let input = form.querySelector('[data-cart-session-recovery-submit]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = 'Checkout';
      input.dataset.cartSessionRecoverySubmit = '';
      form.appendChild(input);
    }
    form.submit();
  }

  function hasPendingQuantityInput() {
    // A typed edit can still be in a component's debounce/bulk queue before
    // mutate() sees it. Do not checkout the old server value during that gap.
    return [...document.querySelectorAll('cart-items, cart-drawer-items, quick-add-bulk, quick-order-list')]
      .some(component => component.queue?.length || component.requestStarted ||
        [...component.querySelectorAll('input[type="number"]')]
          .some(input => input.value !== input.defaultValue));
  }

  function attempt(button, operation) {
    if (leaving) return Promise.resolve(false);
    if (checkoutAttempt) return checkoutAttempt;
    const originalText = button?.textContent;
    const originalDisabled = button?.disabled;
    if (button) { button.disabled = true; button.textContent = 'Checking your cart…'; }
    checkoutAttempt = (async () => {
      try {
        await waitForIdle();
        if (hasPendingQuantityInput()) {
          message = 'Your quantity changes are still updating. Wait a moment, then try again.';
          return false;
        }
        locked = true;
        applyState();
        const version = requestedMutation;
        const intentVersion = revision;
        return await operation(() => version === requestedMutation && intentVersion === revision && !hasPendingQuantityInput());
      }
      catch (error) {
        message = error.cartMessage || 'We could not confirm your cart. Your existing items have not been cleared. Please try again or get help with your order.';
        const current = storage.get();
        if (current) storage.set({ ...current, failed: true });
        return false;
      } finally {
        locked = false;
        checkoutAttempt = null;
        if (button) { button.disabled = Boolean(originalDisabled); button.textContent = originalText; }
        applyState();
      }
    })();
    return checkoutAttempt;
  }

  function preflightCheckout(button) {
    return attempt(button, async (unchanged) => {
      const state = storage.get() || { lines: parseRenderedCart() };
      const actual = await fetchCart();
      if (!actual) throw new Error('Cart verification unavailable');
      if (!unchanged()) { message = 'Your cart is updating. Please try checkout again.'; return false; }
      if (!actual.length || !sameLines(actual, state.lines) || externalChange) {
        storage.set({ failed: true, lines: state.lines });
        message = externalChange ? 'Your cart changed in another tab. Review the current cart before checkout.'
          : !containsLines(actual, state.lines) ? '' : 'Your cart has changed. Review the current cart before checkout.';
        return false;
      }
      storage.set({ failed: false, lines: actual });
      message = '';
      leaving = true;
      submitNormalCheckout(button);
      return true;
    });
  }

  function recoverCheckout(button) {
    return attempt(button, async (unchanged) => {
      const state = storage.get();
      if (!state?.lines.length || externalChange) {
        message = 'Your cart has changed. Review the current cart before restoring items.';
        return false;
      }
      const actual = await fetchCart();
      if (!actual) throw new Error('Cart verification unavailable');
      if (!unchanged() || externalChange) throw new Error('Cart changed');
      const missing = missingLines(actual, state.lines);
      const expected = mergeLines(actual, missing);
      if (missing.length) {
        const items = missing.map(({ selling_plan, ...line }) => selling_plan ? { ...line, selling_plan } : line);
        // Never clear, decrease, or automatically retry an ambiguous POST.
        await request(`${cartUrl()}/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
      }
      const verified = await fetchCart();
      if (!verified || !sameLines(verified, expected) || externalChange || !unchanged()) throw new Error('Restore not confirmed');
      storage.set({ failed: false, lines: verified });
      message = '';
      announceChange();
      leaving = true;
      window.location.assign(cartUrl());
      return true;
    });
  }

  function reviewCart(button) {
    return attempt(button, async (unchanged) => {
      const actual = await fetchCart();
      if (!actual) throw new Error('Cart verification unavailable');
      if (!unchanged()) throw new Error('Cart changed');
      storage.set({ failed: false, lines: actual });
      externalChange = false;
      message = '';
      leaving = true;
      window.location.assign(cartUrl());
      return true;
    });
  }

  function initialize() {
    const rendered = parseRenderedCart();
    const current = storage.get();
    // Version 1 could contain double-counted lines and no customer context.
    // It is deliberately never replayed by version 2.
    if (!current?.failed || containsLines(rendered, current.lines)) storage.set({ failed: false, lines: rendered });
    applyState();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, verifyCartUpdate);
    }
  }

  document.addEventListener('submit', event => {
    const button = event.submitter || event.target?.querySelector?.('button[name="checkout"]');
    if (button?.name !== 'checkout') return;
    event.preventDefault();
    void preflightCheckout(button);
  }, true);

  window.addEventListener('storage', event => {
    if (event.key !== CHANGE_KEY) return;
    externalChange = true;
    message = 'Your cart changed in another tab. Review the current cart before checkout.';
    applyState();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      leaving = false;
      externalChange = true;
      message = 'Review the current cart before checkout.';
      applyState();
    }
  });

  window.PureCartRecovery = {
    applyState, containsLines, mergeLines, normalizeLine, preflightCheckout,
    recoverCheckout, reviewCart, verifyCartUpdate, mutate, getState: () => storage.get(),
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
